/**
 * Which parts of the header and sale details a receipt shows (`app_settings.pos.receipt`, POS.md §18.9). Set in the
 * back office; the till applies them to the receipt data before it fills the template, so the template stays one.
 */
export interface ReceiptOptions {
  show_logo?: boolean
  show_address?: boolean
  show_phone?: boolean
  show_email?: boolean
  show_registration_no?: boolean
  show_tax_id?: boolean
  /** The terminal and cashier line. */
  show_staff?: boolean
  show_customer?: boolean
  /** Absolute logo URL, saved by the back office with the options. Printed by the browser print dialog only. */
  logo_url?: string
}

export const RECEIPT_OPTION_DEFAULTS: Required<Omit<ReceiptOptions, 'logo_url'>> = {
  show_logo: false,
  show_address: true,
  show_phone: true,
  show_email: false,
  show_registration_no: false,
  show_tax_id: true,
  show_staff: true,
  show_customer: true,
}

export const RECEIPT_SWITCHES: { key: keyof typeof RECEIPT_OPTION_DEFAULTS; label: string }[] = [
  { key: 'show_logo', label: 'Logo' },
  { key: 'show_address', label: 'Address' },
  { key: 'show_phone', label: 'Contact number' },
  { key: 'show_email', label: 'Email' },
  { key: 'show_registration_no', label: 'Registration no.' },
  { key: 'show_tax_id', label: 'VAT / BIN' },
  { key: 'show_staff', label: 'Terminal and cashier' },
  { key: 'show_customer', label: 'Customer' },
]

interface ReceiptData {
  shop: Record<string, unknown>
  options?: Record<string, unknown>
  order: Record<string, unknown>
}

/** The receipt data with the switched-off parts left empty, which the template's `if`s then skip. */
export const applyReceiptOptions = <T extends ReceiptData>(data: T, options?: ReceiptOptions | null): T => {
  const o = { ...RECEIPT_OPTION_DEFAULTS, ...(options ?? {}) }
  const logo = o.show_logo && options?.logo_url ? options.logo_url : ''
  return {
    ...data,
    shop: {
      ...data.shop,
      logo,
      address: o.show_address ? data.shop.address : '',
      phone: o.show_phone ? data.shop.phone : '',
      email: o.show_email ? data.shop.email : '',
      registration_no: o.show_registration_no ? data.shop.registration_no : '',
      tax_id: o.show_tax_id ? data.shop.tax_id : '',
    },
    options: { ...data.options, show_logo: !!logo },
    order: {
      ...data.order,
      counter: o.show_staff ? data.order.counter : '',
      cashier: o.show_staff ? data.order.cashier : '',
      customer: o.show_customer ? data.order.customer : null,
    },
  }
}
